import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow } from "@insite/mobius/index";
import React, { FC } from "react";
import { css } from "styled-components";

type OrderLine = {
    lineNo: string;
    partNo: string;
    customerPartNo: string;
    description: string;
    orderedQty: number;
    cancelItemQty: number;
    cancel: boolean;
    notCancellableReason?: string;
};

export interface OrderLinesReviewScreenStyles {
    modalDataTable?: DataTableProps;
}

export const orderLinesReviewScreenStyles: OrderLinesReviewScreenStyles = {
    modalDataTable: {
        mergeCss: true,
        css: css`
            td {
                padding: 14px;
            }

            /* Mobile stacked layout */
            @media (max-width: 640px) {
                &,
                & thead,
                & tbody,
                & th,
                & td,
                & tr {
                    display: block;
                    width: 100%;
                }

                & thead {
                    position: absolute;
                    left: -10000px;
                    top: auto;
                    height: 0;
                    width: 1px;
                    overflow: hidden;
                }

                & tbody {
                    display: block;
                }

                & tr {
                    background: ${themeBackgrounds.white};
                    border: 1px solid ${themeBackgrounds.lightGray};
                    border-radius: 8px;
                    margin-bottom: 12px;
                    padding: 16px;
                }

                & td {
                    border: none !important;
                    padding: 5px;
                    height: auto;
                }
                & td:last-child {
                    border-bottom: none !important;
                }

                & td > .stackedCell {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 12px;
                    width: 100%;
                }

                & td > .stackedCell::before {
                    content: attr(data-label);
                    font-weight: 600;
                    color: ${themeBackgrounds.darkGray};
                    flex: 0 0 auto;
                    text-align: left;
                    max-width: 55%;
                }

                & td > .stackedCell .value {
                    text-align: right;
                    margin-left: auto;
                    word-break: break-word;
                    max-width: 45%;
                    text-wrap: auto;
                }

                & td > .stackedCell .inlineControl {
                    margin-left: auto;
                }
            }
        `,
    },
};

const styles = orderLinesReviewScreenStyles;

type Props = {
    lines: OrderLine[];
};

const OrderLinesReviewScreen: FC<Props> = ({ lines }) => {
    const L_LINE = translate("Line #");
    const L_PART = translate("Part No.");
    const L_CPART = translate("Customer Part No.");
    const L_DESC = translate("Description");
    const L_OQTY = translate("Ordered Qty");
    const L_CQTY = translate("Cancelled Qty");

    return (
        <DataTable {...styles.modalDataTable}>
            <DataTableHead>
                <DataTableHeader tight>{L_LINE}</DataTableHeader>
                <DataTableHeader>{L_PART}</DataTableHeader>
                <DataTableHeader>{L_CPART}</DataTableHeader>
                <DataTableHeader>{L_DESC}</DataTableHeader>
                <DataTableHeader alignX="right">{L_OQTY}</DataTableHeader>
                <DataTableHeader alignX="right">{L_CQTY}</DataTableHeader>
            </DataTableHead>
            <DataTableBody>
                {lines.map(line => (
                    <DataTableRow key={line.lineNo}>
                        <DataTableCell>
                            <div className="stackedCell" data-label={L_LINE}>
                                <span className="value">{line.lineNo}</span>
                            </div>
                        </DataTableCell>

                        <DataTableCell>
                            <div className="stackedCell" data-label={L_PART}>
                                <span className="value">{line.partNo}</span>
                            </div>
                        </DataTableCell>

                        <DataTableCell>
                            <div className="stackedCell" data-label={L_CPART}>
                                <span className="value">{line.customerPartNo}</span>
                            </div>
                        </DataTableCell>

                        <DataTableCell>
                            <div className="stackedCell" data-label={L_DESC}>
                                <span className="value">{line.description}</span>
                            </div>
                        </DataTableCell>

                        <DataTableCell alignX="right">
                            <div className="stackedCell" data-label={L_OQTY}>
                                <span className="value">{line.orderedQty}</span>
                            </div>
                        </DataTableCell>

                        <DataTableCell alignX="right">
                            <div className="stackedCell" data-label={L_CQTY}>
                                <span className="value">{line.cancelItemQty}</span>
                            </div>
                        </DataTableCell>
                    </DataTableRow>
                ))}
                {lines.length === 0 && (
                    <DataTableRow>
                        <DataTableCell colSpan={6} alignX="center">
                            {translate("No items selected.")}
                        </DataTableCell>
                    </DataTableRow>
                )}
            </DataTableBody>
        </DataTable>
    );
};

export default OrderLinesReviewScreen;
